import {
  DamzState,
  RoomUnreadState,
  RoomWindowState,
  RoomStateSnapshot,
} from '../../types';

/**
 * Room state manager utilities for handling unread markers and window states
 * Based on the Room Unread Markers design from README.md
 */

export interface RoomStateTransition {
  from: RoomWindowState;
  to: RoomWindowState;
  condition: string;
}

/**
 * Valid state transitions for the room window state machine
 */
export const ROOM_STATE_TRANSITIONS: RoomStateTransition[] = [
  {
    from: RoomWindowState.ActiveChatting,
    to: RoomWindowState.ScrolledUp,
    condition: 'User scrolls up',
  },
  {
    from: RoomWindowState.ScrolledUp,
    to: RoomWindowState.ActiveChatting,
    condition: 'User scrolls to bottom',
  },
  {
    from: RoomWindowState.ActiveChatting,
    to: RoomWindowState.FocusedAway,
    condition: 'Window loses focus (saves state)',
  },
  {
    from: RoomWindowState.ScrolledUp,
    to: RoomWindowState.FocusedAway,
    condition: 'Window loses focus (saves state)',
  },
  {
    from: RoomWindowState.OpenedAt,
    to: RoomWindowState.FocusedAway,
    condition: 'Window loses focus (saves state)',
  },
  {
    from: RoomWindowState.FocusedAway,
    to: RoomWindowState.ActiveChatting,
    condition: 'Window regains focus (restores previous state)',
  },
  {
    from: RoomWindowState.FocusedAway,
    to: RoomWindowState.ScrolledUp,
    condition: 'Window regains focus (restores previous state)',
  },
  {
    from: RoomWindowState.FocusedAway,
    to: RoomWindowState.OpenedAt,
    condition: 'Window regains focus (restores previous state)',
  },
  {
    from: RoomWindowState.OpenedAt,
    to: RoomWindowState.ActiveChatting,
    condition: 'User scrolls to bottom (including jump to HEAD)',
  },
];

/**
 * Determines if a state transition is valid
 */
export const isValidStateTransition = (
  from: RoomWindowState,
  to: RoomWindowState
): boolean => {
  return ROOM_STATE_TRANSITIONS.some(
    transition => transition.from === from && transition.to === to
  );
};

/**
 * Gets the next valid states for a given current state
 */
export const getValidNextStates = (
  currentState: RoomWindowState
): RoomWindowState[] => {
  return ROOM_STATE_TRANSITIONS.filter(
    transition => transition.from === currentState
  ).map(transition => transition.to);
};

/**
 * Creates a snapshot of the current room state for restoration
 */
export const createStateSnapshot = (
  currentState: RoomUnreadState
): RoomStateSnapshot => {
  return {
    windowState: currentState.windowState,
    unreadMessageCount: currentState.unreadMessageCount,
  };
};

/**
 * Checks if a room has a saved previous state to restore
 */
export const hasPreviousState = (roomState: RoomUnreadState): boolean => {
  return roomState.previousStateSnapshot !== undefined;
};

/**
 * Gets the state to restore when regaining focus
 */
export const getStateToRestore = (
  roomState: RoomUnreadState
): RoomWindowState => {
  return (
    roomState.previousStateSnapshot?.windowState ??
    RoomWindowState.ActiveChatting
  );
};

/**
 * Determines if a room should show unread markers based on window state, focus, and just opened state
 */
export const shouldShowUnreadMarkers = (
  currentState: RoomUnreadState | undefined
): boolean => {
  const { windowState, isWindowFocused, unreadMessageCount, isJustOpened } =
    currentState ?? {};

  if (!unreadMessageCount) {
    return false;
  }

  // Show markers if room was just opened
  if (isJustOpened) {
    return true;
  }

  switch (windowState) {
    case RoomWindowState.ActiveChatting:
      return false; // User is actively chatting, no markers needed
    case RoomWindowState.ScrolledUp:
      return true; // User is scrolled up, show markers
    case RoomWindowState.FocusedAway:
      return !isWindowFocused; // Show markers if window is not focused
    case RoomWindowState.OpenedAt:
      return true; // User opened at specific message, show markers
    default:
      return false;
  }
};

/**
 * Determines if scroll down button should be shown with unread count
 */
export const shouldShowScrollDownButton = (
  currentState: RoomUnreadState | undefined
): boolean => {
  const { windowState, unreadMessageCount } = currentState ?? {};

  if (!unreadMessageCount) {
    return false;
  }

  switch (windowState) {
    case RoomWindowState.ScrolledUp:
      return true; // User is scrolled up, show scroll down button
    case RoomWindowState.OpenedAt:
      return true; // User opened at specific message, show scroll down button
    default:
      return false;
  }
};

/**
 * Determines if unread count should be cleared when new messages arrive
 */
export const shouldClearUnreadCount = (
  currentState: RoomUnreadState | undefined
): boolean => {
  const { windowState } = currentState ?? {};

  switch (windowState) {
    case RoomWindowState.ActiveChatting:
      return true; // User is actively chatting, clear counts
    case RoomWindowState.ScrolledUp:
      return false; // User is scrolled up, don't clear counts
    case RoomWindowState.FocusedAway:
      return false; // Don't clear counts when window is focused away
    case RoomWindowState.OpenedAt:
      return false; // User opened at specific message, don't clear
    default:
      return false;
  }
};

/**
 * Determines if last viewed message should be updated when new messages arrive
 */
export const shouldUpdateLastViewedMessage = (
  currentState: RoomUnreadState | undefined
): boolean => {
  const { windowState, isWindowFocused = true } = currentState ?? {};

  switch (windowState) {
    case RoomWindowState.ActiveChatting:
      return true; // User is actively chatting, update last viewed
    case RoomWindowState.ScrolledUp:
      return false; // User is scrolled up, don't update
    case RoomWindowState.FocusedAway:
      return isWindowFocused; // Update if window is focused TODO probably should be false
    case RoomWindowState.OpenedAt:
      return false; // User opened at specific message, don't update
    default:
      return false;
  }
};

export const shouldAppendNewMessages = (
  currentState: RoomUnreadState | undefined
): boolean => {
  const { windowState, previousStateSnapshot } = currentState ?? {};
  return !(
    windowState === RoomWindowState.OpenedAt ||
    (windowState === RoomWindowState.FocusedAway &&
      previousStateSnapshot?.windowState === RoomWindowState.OpenedAt)
  );
};

/**
 * Get defaults for first initialization of roomUnread state
 * else use current state
 */
export const getInitialUnreadRoomState = (
  state: DamzState['roomUnreadState'],
  room_id: string
): RoomUnreadState => {
  const currentState = state[room_id];
  if (!currentState || !shouldShowUnreadMarkers(currentState)) {
    return {
      unreadMessageCount: 0,
      isJustOpened: true,
      isWindowFocused: true,
      windowState: RoomWindowState.ActiveChatting,
    };
  }
  return currentState;
};

/**
 * Updates room state when new messages arrive
 */
export const updateRoomStateForNewMessages = (
  currentState: RoomUnreadState | undefined,
  newUnreadCount: number
): Partial<RoomUnreadState> => {
  const shouldClear = shouldClearUnreadCount(currentState);

  return {
    unreadMessageCount: shouldClear
      ? 0
      : (currentState?.unreadMessageCount ?? 0) + newUnreadCount,
    isJustOpened: false, // Clear just opened state when new messages arrive
  };
};

/**
 * Updates room state when user scrolls to bottom
 */
export const updateRoomStateForScrollToBottom = (
  currentState: RoomUnreadState
): Partial<RoomUnreadState> => {
  return {
    unreadMessageCount: 0,
    windowState: RoomWindowState.ActiveChatting,
    isJustOpened: false, // Clear just opened state when user scrolls to bottom
  };
};

/**
 * Updates room state when user scrolls up
 */
export const updateRoomStateForScrollUp = (
  currentState: RoomUnreadState
): Partial<RoomUnreadState> => {
  return {
    windowState: RoomWindowState.ScrolledUp,
  };
};

/**
 * Updates room state when user opens at a specific message
 */
export const updateRoomStateForOpenedAt = (
  currentState: RoomUnreadState
): Partial<RoomUnreadState> => {
  return {
    windowState: RoomWindowState.OpenedAt,
  };
};

/**
 * Updates room state when window focus changes
 */
export const updateRoomStateForFocusChange = (
  currentState: RoomUnreadState,
  isWindowFocused: boolean
): Partial<RoomUnreadState> => {
  if (isWindowFocused) {
    // Gaining focus: restore from snapshot if available
    if (currentState.previousStateSnapshot) {
      const snapshot = currentState.previousStateSnapshot;
      return {
        isWindowFocused: true,
        windowState: snapshot.windowState,
        previousStateSnapshot: undefined, // Clear snapshot after restoring
      };
    } else {
      // No snapshot, default to ActiveChatting
      return {
        isWindowFocused: true,
        windowState: RoomWindowState.ActiveChatting,
      };
    }
  } else {
    // Losing focus: save current state as snapshot
    const snapshot = createStateSnapshot(currentState);
    return {
      isWindowFocused: false,
      windowState: RoomWindowState.FocusedAway,
      previousStateSnapshot: snapshot,
    };
  }
};
